import { usePersistedState } from '@/lib/use-persisted-state'
import type { ViewMode } from '@/ui/view-switcher'

interface UseViewModeOptions {
  storageKey: string // e.g., 'journals-view-mode'
  defaultMode: ViewMode // e.g., 'timeline'
  availableModes?: ViewMode[] // e.g., ['list', 'timeline'] (no grid for journals)
}

/**
 * Hook for managing route-specific, persisted view mode state.
 *
 * Features:
 * - Persists to localStorage with route-specific key
 * - Validates stored value against available modes
 * - Falls back to default if stored value is invalid
 *
 * @example
 * ```tsx
 * const { viewMode, setViewMode, availableModes } = useViewMode({
 *   storageKey: 'journals-view-mode',
 *   defaultMode: 'timeline',
 *   availableModes: ['list', 'timeline']
 * })
 * ```
 */
export function useViewMode({
  storageKey,
  defaultMode,
  availableModes = ['list', 'grid', 'timeline'],
}: UseViewModeOptions) {
  const [viewMode, setViewMode] = usePersistedState<ViewMode>(
    storageKey,
    defaultMode,
  )

  // Ensure stored mode is valid for available options
  // (handles case where user previously selected 'grid' but it's no longer available)
  const validMode = availableModes.includes(viewMode) ? viewMode : defaultMode

  return {
    viewMode: validMode,
    setViewMode,
    availableModes,
  }
}
